/**
 * Mode implementation computes the current mode based on URL, state, and preferences.
 * For now, it primarily derives sharedScreen from URL patterns, but will be extended
 * to aggregate multiple mode sources in the future.
 */
import { signal, computed } from '@preact/signals-react'
import type { Mode, ModeDeps } from './mode.types'
import { makeFocusNavigation } from '../focusNavigation/focusNavigation'
import { getURLInfo, isOmniModeFromRoomName, isSharedScreenModeFromRoomName } from '../urlInfo/urlInfo'

export function makeMode(deps: ModeDeps): Mode {
  const { controllerInput } = deps

  // Internal signal for shared screen mode
  const sharedScreenSignal = signal<boolean>(isSharedScreenModeFromRoomName(getURLInfo().roomName))
  // Omni mode starts from the `test:` URL harness token, but (unlike shared-screen)
  // can also be flipped on at runtime via applyOmniMode() - see its doc comment.
  const omniModeSignal = signal<boolean>(isOmniModeFromRoomName(getURLInfo().roomName))
  // Pathname signal ensures computed re-runs when URL changes via pushState (e.g. leave room).
  // popstate only updates sharedScreenSignal/omniModeSignal, which may not change (e.g. Omni mode -> /).
  const pathnameSignal = signal<string>(
    typeof window !== 'undefined' ? window.location.pathname : ''
  )

  // Watch for URL changes
  if (typeof window !== 'undefined') {
    sharedScreenSignal.value = isSharedScreenModeFromRoomName(getURLInfo().roomName)
    omniModeSignal.value = isOmniModeFromRoomName(getURLInfo().roomName)

    const syncFromUrl = () => {
      pathnameSignal.value = window.location.pathname
      sharedScreenSignal.value = isSharedScreenModeFromRoomName(getURLInfo().roomName)
      // Only let the URL turn Omni mode *on* (e.g. navigating to a `test:` harness
      // link) - never use popstate to clear a runtime-applied toggle. A normal
      // networked room URL never carries the `test:` marker, so unconditionally
      // syncing here would silently turn off a toggle applied via applyOmniMode()
      // on any browser back/forward navigation. exitOmniMode() is the only
      // intended way to turn it off once applied at runtime.
      if (isOmniModeFromRoomName(getURLInfo().roomName)) {
        omniModeSignal.value = true
      }
    }
    window.addEventListener('popstate', syncFromUrl)
  }

  // Computed mode that aggregates all mode attributes
  const computedMode = computed<{ sharedScreen: boolean; darkMode: boolean; localMode: boolean; omniMode: boolean }>(() => {
    void pathnameSignal.value // subscribe so computed re-runs when pathname changes
    const urlInfo = getURLInfo()
    return {
      sharedScreen: sharedScreenSignal.value,
      darkMode: false, // Placeholder - will be migrated from theme system later
      localMode: urlInfo.isLocalMode,
      omniMode: omniModeSignal.value,
    }
  })
  
  function setSharedScreen(enabled: boolean): void {
    sharedScreenSignal.value = enabled
  }

  const focusNavigation = makeFocusNavigation()
  const unsubscribeControllerFocusNavigation = controllerInput.subscribeRaw((raw) => {
    focusNavigation.enableFromControllerUsage(raw, computedMode.value.sharedScreen)
  })

  // Auto-enable focus navigation in shared screen mode
  // This is essential for controller-based interaction
  if (sharedScreenSignal.value && !focusNavigation.isActive.value) {
    focusNavigation.enable()
  }

  const isGamePaused = signal<boolean>(false)

  function isLocalMode(): boolean {
    return computedMode.value.localMode
  }

  function isOmniMode(): boolean {
    return computedMode.value.omniMode
  }

  function applyOmniMode(): void {
    omniModeSignal.value = true
  }

  function exitOmniMode(): void {
    omniModeSignal.value = false
  }

  function isSharedScreenMode(): boolean {
    return computedMode.value.sharedScreen
  }

  function getAutoStartGameName(): string | null {
    if (typeof window === 'undefined') return null

    // Extract game ID and (optional) state name from URL path
    // Format: /r/<roomName>/<gameId>/<stateName> or /r/<roomName>/<gameId>
    const pathMatch = window.location.pathname.match(/\/r\/[^\/]+\/([^\/]+)(?:\/([^\/]+))?$/)
    const gameIdFromURL = pathMatch ? decodeURIComponent(pathMatch[1]) : null

    // If gameId is in URL path, always use it
    if (gameIdFromURL) return gameIdFromURL

    // In Omni mode, allow hash/query based autostart
    if (isOmniMode()) {
      const hash = window.location.hash.slice(1)
      const params = new URLSearchParams(window.location.search)
      return hash || params.get('game') || null
    }

    return null
  }
  
  return {
    computedMode,
    setSharedScreen,
    isLocalMode,
    isOmniMode,
    applyOmniMode,
    exitOmniMode,
    isSharedScreenMode,
    getAutoStartGameName,
    isGamePaused,
    focusNavigation,
    controllerFocusNavigation: {
      stop: unsubscribeControllerFocusNavigation,
    },
  }
}
